import {
  Stack,
  Typography,
  IconButton,
  Button,
  LinearProgress,
} from "@mui/material";
import { formatSize, toLocaleDateTimeString } from "@utils";
import {
  UploadFile as UploadFileIcon,
  VideoFile as VideoFileIcon,
  Image as ImageIcon,
  Description as DescriptionIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import {
  Attachment,
  AttachmentCategory,
  isNotDeleted,
} from "@utils/useManageAttachments";
import { useMemo } from "react";

const AttachmentIcon = ({
  attachmentKind,
  isValid,
}: {
  attachmentKind: AttachmentCategory;
  isValid: boolean;
}) => {
  const color = isValid ? "primary" : "error";
  switch (attachmentKind) {
    case "scorecard":
      return <DescriptionIcon color={color} />;
    case "walkthrough":
      return <VideoFileIcon color={color} />;
    case "image":
      return <ImageIcon color={color} />;
    default:
      return <UploadFileIcon color="error" />;
  }
};

const getStatusText = (attachment: Attachment) => {
  switch (attachment.state) {
    case "uploading":
      return "Uploading";
    case "completed":
      if (attachment.toDelete) {
        return "Deleted";
      } else if (attachment.sentForReview) {
        return "Sent";
      } else {
        return "Ready";
      }
    case "pending":
      return "Ready";
    default:
      return "Error";
  }
};

interface Props {
  attachmentKey: string;
  attachment: Attachment;
  onDelete: (key: string, attachment: Attachment) => void;
  openPreview: (attachment: Attachment) => void;
  isEditMode: boolean;
  disabled: boolean;
}

const OrderTrackingFileInfo = ({
  attachmentKey,
  attachment,
  onDelete,
  openPreview,
  isEditMode,
  disabled,
}: Props) => {
  const isPending = attachment.state === "pending";
  const isUploading = attachment.state === "uploading";
  const isRejected = attachment.state === "rejected";
  const isError = attachment.state === "error";
  const isValid = !isRejected && !isError;
  const isScorecard = attachment.category === "scorecard";
  const validAndNotDeleted = isValid && isNotDeleted(attachment);
  const rejectedAndNotDeleted = isRejected && isNotDeleted(attachment);

  const fileInfoString = useMemo(() => {
    if (isError) {
      return attachment.message;
    }
    if (isRejected) {
      return attachment.reasons.join(", ");
    }
    const fileSizeText = formatSize(attachment.size);
    const statusText = getStatusText(attachment);
    const lastModifiedText = toLocaleDateTimeString(attachment.lastModified);
    return `${fileSizeText} • ${statusText} • ${lastModifiedText}`;
  }, [attachment, isError, isRejected]);

  const deleteDisabled =
    !isEditMode || disabled || isUploading || (isPending && !isScorecard);
  const deleteVisible =
    isEditMode && (validAndNotDeleted || rejectedAndNotDeleted);

  return (
    <Stack
      direction="row"
      sx={{
        alignItems: "center",
        padding: 1,
        gap: 2,
      }}
    >
      <AttachmentIcon
        attachmentKind={attachment.category}
        isValid={validAndNotDeleted}
      />
      <Stack
        sx={{
          flex: 1,
        }}
      >
        <Typography
          color={validAndNotDeleted ? "text.primary" : "error"}
          variant="body1"
        >
          {attachment.name}
        </Typography>
        <Typography
          color={validAndNotDeleted ? "text.secondary" : "error"}
          variant="body2"
        >
          {fileInfoString}
        </Typography>
        {isUploading && (
          <LinearProgress
            variant="determinate"
            value={attachment.progress}
            sx={{ height: "4px", width: "200px" }}
          />
        )}
      </Stack>
      {validAndNotDeleted && openPreview && !disabled && (
        <Button variant="text" onClick={() => openPreview(attachment)}>
          Preview
        </Button>
      )}
      {deleteVisible && (
        <IconButton
          onClick={() => onDelete(attachmentKey, attachment)}
          size="small"
          disabled={deleteDisabled}
          color="secondary"
        >
          <DeleteIcon />
        </IconButton>
      )}
    </Stack>
  );
};

export default OrderTrackingFileInfo;
